import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, MenuItem, Button, Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Chip, InputAdornment } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SecurityIcon from '@mui/icons-material/Security';
import api from '../../api';

const LoginAudit = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departments, setDepartments] = useState([]);

  const today = new Date();
  const defaultStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const defaultEnd = today.toISOString().split('T')[0];

  const [filters, setFilters] = useState({ search: '', start_date: defaultStart, end_date: defaultEnd, department_id: '' });

  useEffect(() => {
    // Component now trusts your <ProtectedRoute> in App.js to handle security.
    api.get('/correction-dashboard/filters-data').then(res => setDepartments(res.data.departments)).catch(console.error);
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const fetchAuditLogs = async () => {
    const query = new URLSearchParams({ ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')), page, rowsPerPage }).toString();
    try {
      const res = await api.get(`/login-audit/list?${query}`);
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (err) { console.error("Failed to fetch logs", err); }
  };

  const handleExport = () => {
    const query = new URLSearchParams(Object.entries(filters).filter(([_, v]) => v !== '')).toString();
    window.open(`http://${window.location.hostname}:8000/api/v1/login-audit/export?${query}`, '_blank');
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f4f6f8', minHeight: '90vh' }}>
      
      {/* Title Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, bgcolor: '#ffffff', p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <SecurityIcon sx={{ color: '#2e7d32', mr: 1.5, fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10394f' }}>
          System Login Audits
        </Typography>
      </Box>

      {/* FILTER BAR */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderRadius: 2 }} elevation={0} variant="outlined">
        <TextField 
          size="small" placeholder="Search Name or ID" 
          value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 220, bgcolor: '#ffffff' }}
        />
        <TextField size="small" type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={filters.start_date} onChange={e => setFilters({...filters, start_date: e.target.value})} sx={{ bgcolor: '#ffffff' }} />
        <TextField size="small" type="date" label="End Date" InputLabelProps={{ shrink: true }} value={filters.end_date} onChange={e => setFilters({...filters, end_date: e.target.value})} sx={{ bgcolor: '#ffffff' }} />
        <TextField select size="small" label="Department" sx={{ minWidth: 180, bgcolor: '#ffffff' }} value={filters.department_id} onChange={e => setFilters({...filters, department_id: e.target.value})}>
          <MenuItem value="">All Departments</MenuItem>
          {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.department_name}</MenuItem>)}
        </TextField>
        
        <Button variant="contained" onClick={() => { setPage(0); fetchAuditLogs(); }} disableElevation sx={{ bgcolor: '#1976d2', textTransform: 'none' }}>Filter Results</Button>
        <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleExport} sx={{ ml: 'auto', textTransform: 'none', color: '#333', borderColor: '#ccc' }}>Export</Button>
      </Paper>

      {/* DATA TABLE */}
      <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f9fafb' }}>
              <TableCell sx={{ fontWeight: 'bold', color: '#555', borderBottom: '2px solid #eee' }}>Date & Time</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#555', borderBottom: '2px solid #eee' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#555', borderBottom: '2px solid #eee' }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#555', borderBottom: '2px solid #eee' }}>OS / Device</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#555', borderBottom: '2px solid #eee' }}>IP & Location</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#555', borderBottom: '2px solid #eee' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>No audit logs found.</TableCell></TableRow> : data.map(row => (
              <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell sx={{ color: '#444' }}>{row.formatted_time}</TableCell>
                
                {/* Stacked Name & Email */}
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#222' }}>{row.name}</Typography>
                  <Typography variant="caption" sx={{ color: '#777' }}>{row.email || 'No email provided'}</Typography>
                </TableCell>
                
                <TableCell sx={{ color: '#444' }}>{row.department_name || '--'}</TableCell>
                <TableCell sx={{ color: '#444' }}>{row.os_device || 'Unknown Device'}</TableCell>
                
                {/* Stacked IP & Location */}
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#222' }}>{row.ip_address || '--'}</Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>{row.location}</Typography>
                </TableCell>
                
                {/* Custom Styled Status Badges */}
                <TableCell>
                  {row.status === 'Success' ? (
                    <Chip 
                      icon={<CheckCircleOutlineIcon style={{ fontSize: 16 }} />} 
                      label="Success" 
                      size="small"
                      sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 'bold', px: 0.5, '& .MuiChip-icon': { color: '#2e7d32' } }} 
                    />
                  ) : (
                    <Chip 
                      icon={<HighlightOffIcon style={{ fontSize: 16 }} />} 
                      label="Failed" 
                      size="small"
                      sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 'bold', px: 0.5, '& .MuiChip-icon': { color: '#c62828' } }} 
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination 
          component="div" count={total} page={page} rowsPerPage={rowsPerPage} 
          onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} 
          sx={{ borderTop: '1px solid #eee' }}
        />
      </Paper>
    </Box>
  );
};

export default LoginAudit;